import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HeartIcon, ShieldCheckIcon, BookOpenIcon, SmileIcon, CheckCircleIcon } from 'lucide-react';
export function ColostomyCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-470">
      <PageBanner title="Colostomy & Ostomy Care" subtitle="Comprehensive support and education for patients with bowel diversions." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Colostomy Care',
      path: '/services/colostomy-care'
    }]} data-id="element-471" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-472">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-474">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-475">
              <HeartIcon className="h-4 w-4 text-primary" data-id="element-476" />
              <span className="text-primary text-sm font-semibold" data-id="element-477">
                Compassionate Support
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-478">
              Empowering Patients Through Expert Care
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-479">
              <p data-id="element-480">
                Our team provides comprehensive support for patients with bowel
                diversions. We focus on skin integrity, appliance management,
                and empowering patients through education on lifestyle
                adjustments and self-care.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-481">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-482" />
              <div data-id="element-483">
                <h4 className="font-bold text-dark" data-id="element-484">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-485">
                  All ostomy care plans are developed and supervised by
                  experienced Registered Nurses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-486">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-487">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-488">
            <div className="text-center mb-14" data-id="element-489">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-490">
                Comprehensive Ostomy Support
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-491"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-492">
              {[{
              icon: ShieldCheckIcon,
              title: 'Skin Integrity',
              desc: 'Proactive management to prevent irritation and maintain healthy peristomal skin.'
            }, {
              icon: HeartIcon,
              title: 'Appliance Management',
              desc: 'Expert assistance with pouching systems to ensure a secure and comfortable fit.'
            }, {
              icon: BookOpenIcon,
              title: 'Patient Education',
              desc: 'Teaching self-care techniques to build confidence and independence.'
            }, {
              icon: SmileIcon,
              title: 'Lifestyle Support',
              desc: 'Guidance on diet, clothing, and activities to help you live life to the fullest.'
            }].map((benefit, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-493">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-494">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-495" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-496">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-497">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-498">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-499">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-500">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-501">
              Our Ostomy Care Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-502">
              Adjusting to life with an ostomy can be challenging. Our
              compassionate team is here to provide the physical care and
              emotional support you need to adapt successfully.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-503">
              {['Ostomy appliance changes & management', 'Peristomal skin assessment', 'Patient & family education', 'Lifestyle adjustment guidance', 'Output monitoring', 'Self-care skill building'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light-alt rounded-xl border border-gray-100" data-id="element-504">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-505" />
                  <span className="text-dark font-medium" data-id="element-506">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light-alt" data-id="element-507">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-508">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-509">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-510"></div>
            <div className="relative z-10" data-id="element-511">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-512">
                Need Support with Ostomy Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-513">
                Contact us today to discuss your care needs and schedule a
                consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-514">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-515" />
    </main>;
}